<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { type LocationQueryValueRaw, useRoute, useRouter } from 'vue-router';

import { useMultiClusterStore } from '@/stores/multiCluster';

const route = useRoute();
const router = useRouter();
const multiClusterStore = useMultiClusterStore();

const clusterName = ref(route.query.cl);

// move primary to front of list
const sortedClusters = computed(() => {
  const sorted = multiClusterStore.clusters?.filter((c) => !c.spec?.primary);
  if (multiClusterStore.primaryCluster) sorted?.unshift(multiClusterStore.primaryCluster);
  return sorted;
});
const isMCPage = computed(() => !!route.meta?.isMC);
const selectedCluster = computed({
  get: () => clusterName.value,
  set: (newVal) => handleChange(newVal),
});

watch(
  () => multiClusterStore.primaryCluster,
  (newVal) => {
    // if we are not on an MC page & no ?cl is selected, choose the primary cluster
    if (newVal && !isMCPage.value && !selectedCluster.value) {
      router.push({
        name: route.name ?? '',
        query: { ...route.query, cl: newVal.metadata?.name },
      });
    }
  },
  { immediate: true },
);
// if cl is changed in url bar update the selector
watch(
  () => route.query.cl,
  (newVal) => {
    clusterName.value = newVal;
  },
);

function handleChange(clusterName: LocationQueryValueRaw | LocationQueryValueRaw[]) {
  // if on a single cluster page and changed to Multi-Cluster
  // route to clusters page
  if (!isMCPage.value && !clusterName) {
    router.push({ name: 'MCClusters', query: { ...route.query, cl: undefined } });
    return;
  }
  // if on a multi-cluster page and changed to a proxied cluster
  // route to dashboard
  if (isMCPage.value && clusterName) {
    router.push({ name: 'Dashboard', query: { ...route.query, cl: clusterName } });
    return;
  }
  // otherwise stay on the same page and update the query.cl
  router
    .push({
      name: route.name ?? '',
      query: { ...route.query, cl: clusterName },
    })
    .then(() => window.location.reload());
}

onMounted(() => {
  if (multiClusterStore.clusters === null) {
    multiClusterStore.fetchClusters();
  }
});
</script>
<template>
  <select
    v-model="selectedCluster"
    id="selectCluster"
    class="form-select mt-0 block w-full rounded border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-200 focus:ring-2 focus:ring-blue-400 dark:bg-navy-800 dark:text-white dark:ring-white/5 dark:focus:border-blue-500 dark:focus:ring-blue-500 sm:text-sm sm:leading-6"
    aria-label="Choose Multi-Cluster Context"
  >
    <option :value="undefined">Multi-Cluster Manager</option>
    <optgroup label="Clusters">
      <option
        v-for="cluster in sortedClusters"
        :key="cluster.metadata?.uid"
        :value="cluster.metadata?.name"
        :disabled="
          // don't disable primaries, check that secondary ingress is set
          !cluster.spec?.primary && !cluster.spec?.k10?.ingress?.url
        "
      >
        {{ cluster.metadata?.name }} {{ cluster.spec?.primary ? '*' : null }}
      </option>
    </optgroup>
  </select>
</template>

<style scoped></style>
